import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  MessageCircle, 
  Send,
  Sparkles,
  CheckCircle,
  HelpCircle,
  Clock,
  Lightbulb,
  Shield,
  ArrowRight,
  Euro,
  Hammer,
  FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function FeatureChat() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    navigate(user ? '/chat' : '/auth');
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Assistant Travaux IA | Conseils Rénovation 24h/24 | Assistant by Léon</title>
        <meta name="description" content="Posez vos questions travaux à Léon, votre assistant IA spécialisé en rénovation. Conseils personnalisés, prix du marché, techniques et normes disponibles 24h/24." />
        <meta name="keywords" content="conseils travaux, questions rénovation, aide bricolage, prix travaux, assistant rénovation, conseiller travaux" />
        <link rel="canonical" href="https://assistantbyleon.fr/fonctionnalite/assistant" />
        <meta property="og:title" content="Assistant Travaux IA - Conseils Rénovation 24h/24" />
        <meta property="og:description" content="Posez n'importe quelle question sur vos travaux. Léon vous répond avec des conseils d'expert, disponible jour et nuit." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {/* Section 1: Accroche SEO optimisée */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/20 rounded-full mb-6"
          >
            <MessageCircle className="w-5 h-5 text-gold-400" />
            <span className="text-gold-400 font-medium">Assistant travaux intelligent</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6"
          >
            Votre conseiller travaux{' '}
            <span className="text-gold-500">disponible 24h/24</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
          >
            Posez n'importe quelle question sur vos <strong className="text-white">travaux de rénovation</strong>.
            Léon vous répond avec des <strong className="text-white">conseils d'expert</strong>, 
            les <strong className="text-white">prix du marché</strong> et les <strong className="text-white">bonnes pratiques</strong>.
            <span className="block mt-2 text-gold-400">Des réponses claires, même pour les débutants.</span>
          </motion.p>
        </div>
      </section>

      {/* Section 2: Comment utiliser l'assistant */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Comment fonctionne l'assistant Léon ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto"
          >
            Comme discuter avec un ami expert en travaux, mais disponible à n'importe quelle heure.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                step: '1', 
                title: 'Posez votre question', 
                desc: 'Écrivez simplement ce que vous voulez savoir sur vos travaux.',
                icon: HelpCircle,
                color: 'gold'
              },
              { 
                step: '2', 
                title: 'Léon analyse et répond', 
                desc: 'L\'IA spécialisée en rénovation formule une réponse adaptée.',
                icon: Sparkles,
                color: 'blue'
              },
              { 
                step: '3', 
                title: 'Approfondissez', 
                desc: 'Continuez la conversation pour avoir plus de détails.',
                icon: Send,
                color: 'emerald'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${item.color}-500/10 border border-${item.color}-500/20 flex items-center justify-center`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-400`} />
                </div>
                <div className="text-gold-500 font-bold text-sm mb-2">Étape {item.step}</div>
                <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Exemples de questions */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-8"
          >
            Exemples de questions à poser à Léon
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 border-gold-500/20"
          >
            <div className="space-y-6">
              {/* Question Prix */}
              <div className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800/70 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <Euro className="w-5 h-5 text-emerald-400" />
                  <span className="text-gold-400 text-sm font-medium">Question prix</span>
                </div>
                <p className="text-white font-medium mb-2">"Combien coûte la pose de carrelage au m² ?"</p>
                <p className="text-zinc-400 text-sm">→ Léon vous donne une fourchette de prix avec les facteurs qui influencent le tarif</p>
              </div>
              
              {/* Question Technique */}
              <div className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800/70 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <Hammer className="w-5 h-5 text-blue-400" />
                  <span className="text-gold-400 text-sm font-medium">Question technique</span>
                </div>
                <p className="text-white font-medium mb-2">"Comment préparer un mur avant de peindre ?"</p>
                <p className="text-zinc-400 text-sm">→ Léon détaille les étapes : nettoyage, rebouchage, sous-couche...</p>
              </div>
              
              {/* Question Choix */}
              <div className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800/70 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <HelpCircle className="w-5 h-5 text-purple-400" />
                  <span className="text-gold-400 text-sm font-medium">Question choix</span>
                </div>
                <p className="text-white font-medium mb-2">"Parquet massif ou contrecollé pour une chambre ?"</p>
                <p className="text-zinc-400 text-sm">→ Léon compare les avantages/inconvénients et vous aide à décider</p>
              </div>
              
              {/* Question Norme */}
              <div className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800/70 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  <span className="text-gold-400 text-sm font-medium">Question norme</span>
                </div>
                <p className="text-white font-medium mb-2">"Quelles sont les normes pour une prise électrique en salle de bain ?"</p>
                <p className="text-zinc-400 text-sm">→ Léon explique les zones et distances réglementaires</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Avantages de l'assistant */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Pourquoi utiliser l'assistant Léon ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-10 max-w-2xl mx-auto"
          >
            Un expert travaux dans votre poche, sans rendez-vous ni déplacement.
          </motion.p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Clock, title: 'Disponible 24h/24', desc: 'Réponses instantanées à toute heure', color: 'blue' },
              { icon: Lightbulb, title: 'Conseils d\'expert', desc: 'Réponses basées sur les bonnes pratiques', color: 'gold' },
              { icon: CheckCircle, title: 'Réponses claires', desc: 'Explications simples et pédagogiques', color: 'emerald' },
              { icon: Shield, title: 'Fiable', desc: 'IA spécialisée dans le bâtiment', color: 'purple' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card-dark p-5 text-center"
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-${item.color}-500/10 flex items-center justify-center`}>
                  <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <h3 className="text-white font-bold mb-1">{item.title}</h3>
                <p className="text-zinc-500 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: CTA Final */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Une question sur vos travaux ?
          </h2>
          <p className="text-zinc-400 mb-8">
            Léon est là pour vous aider, 24h/24 et 7j/7.
            <span className="block text-gold-400 mt-2">5 questions gratuites pour commencer.</span>
          </p>
          
          <Button
            onClick={handleCTA}
            className="px-10 py-6 text-lg bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 text-black font-bold"
            data-testid="cta-chat"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Poser ma question
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-zinc-600 text-xs mt-4">
            Gratuit • Sans engagement • Réponse immédiate
          </p>
        </motion.div>
      </section>

    </div>
  );
}
